import React, { useState } from "react"
import { Button, InputNumber, Segmented, Select, Switch, Tag } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faEye,
  faEyeSlash,
  faThumbtack,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { showsSiblings } from "../../compiler/table"
import { isYesNoField } from "../../model/dataVariables"
import { kindsForQuestion } from "../../model/defaults"
import type { RangeItem } from "../../model/ranges"
import { positionScores } from "../../model/scale"
import { shownNames } from "../../model/shownLabels"
import { selectPlan, useTabSpecStore } from "../../runtime/tabSpecStore"
import { Catalogue, CatalogueQuestion, findQuestion } from "../../types/catalogue"
import {
  isRangeRow,
  RangeRow,
  RowItem,
  SiblingLayout,
  TableKind,
} from "../../types/tabSpecDocument"
import { KIND_TEXT } from "../Grid/describe"
import RangeListEditor from "../shared/RangeListEditor.component"
import AddDifferenceModal from "./AddDifferenceModal.component"
import AddNetModal from "./AddNetModal.component"
import DraftInput from "./DraftInput.component"
import Field from "./Field.component"
import GridSummaryRows from "./GridSummaryRows.component"
import RowOrder from "./RowOrder.component"
import {
  addDifference,
  addMean,
  addNet,
  addNps,
  materialise,
  moveRow,
  removeItem,
  renameRow,
  setAnchor,
  setHidden,
} from "./rowEdits"
import { useInspectedTable } from "./useInspectedTable"

const FOLLOWING =
  "Following the questionnaire. New answer options appear by themselves."
const FIXED = "Fixed list. New answer options are added at the end and flagged."
/** The same for a table of a variable from the data, which has no questionnaire. */
const FOLLOWING_DATA = "Following the prepared data. New categories appear by themselves."
const FIXED_DATA = "Fixed list. New categories are added at the end and flagged."
const YES_NO = "One yes or no field: the rows are always Yes and No, and cannot be changed."
/** Under Show as, for a scale from the data: its fields need not exclude each other. */
const DATA_SCALE =
  "Shown as a scale, a table from the data gets its mean only when the engine confirms that no respondent in the base has more than one category marked."
/** Under the switch and the ranges of a number table. */
const EACH_VALUE =
  "Each value given is a row with its count and percentage, above the ranges and the statistics. Only whole numbers whose values span 250 or less can be shown so."
const NO_RANGES = "No ranges: the table shows the statistics."
const RANGES =
  "Each range is a row with its count and percentage, above the statistics. A range includes both its from and its to value."
const SCORES_HINT = "Give each option a number to get a mean."
/** The title of the pin of an exception, which is always kept at the end. */
const KEPT_EXCEPTION = "An exception always stays at the end when sorting."
const DATA_SCORES_HINT =
  "Give each category a number to get a mean. A category without a number is left out of the mean."

/**
 * Why a table from the data has no rows to show, as the compiler says why it
 * skips: the questionnaire took the name, the prepared data does not have
 * the variable, or nothing is known yet (no questionnaire or no data).
 * `shown`: the variable as the tables show it.
 */
const goneFromData = (variable: string, shown: string, catalogue: Catalogue | null): string =>
  catalogue && findQuestion(catalogue, variable)
    ? `${shown} is a data variable, but the questionnaire now has a question with that name.`
    : catalogue?.knownPaths
      ? `${shown} is not in the prepared data. Check the preparation, or remove the table.`
      : "This table comes from the data. Its rows are known once the data is imported and prepared."

/** What a row is: an option by its label, which is its identity; a net or a difference by its name. */
const rowName = (r: RowItem): string =>
  r.kind === "option" ? r.label : r.kind === "mean" ? "Mean" : r.name

/** The layouts of a grid of the data shown as one table, in the inspector's words. */
const LAYOUT_OPTIONS: { value: SiblingLayout; label: string }[] = [
  { value: "stacked", label: "Stacked" },
  { value: "row", label: "One row each" },
]

/** "a", "a and b", "a, b and c"; past four, "a, b, c and 5 more". */
const listNames = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? ""
  if (items.length > 4) return `${items.slice(0, 3).join(", ")} and ${items.length - 3} more`
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
}

/**
 * The rows the "One row each" layout can show for every row of the grid:
 * the options shown (by their label, which the table stores), the nets, the
 * difference rows and the mean, in the table's order, each named as shown.
 */
const siblingRowChoices = (
  rows: RowItem[],
  optionName: (label: string) => string
): { value: string; label: string; title: string; search: string }[] =>
  rows.flatMap((r) => {
    if (r.kind === "option") {
      if (r.hidden) return []
      const name = r.rename?.trim() || optionName(r.label)
      const search = name === r.label ? name : `${name}\n${r.label}`
      return [{ value: r.label, label: name, title: r.label, search }]
    }
    const name = r.kind === "mean" ? "Mean" : r.name.trim()
    return name ? [{ value: name, label: name, title: name, search: name }] : []
  })

/** The current numbers in reverse order over the scored options: the first option takes the last one's number. */
const reverseScores = (
  q: CatalogueQuestion,
  scores: Record<string, number>
): Record<string, number> => {
  const labels = q.options
    .filter((o) => !o.exception && o.label in scores)
    .map((o) => o.label)
  const values = labels.map((l) => scores[l]).reverse()
  return { ...scores, ...Object.fromEntries(labels.map((l, i) => [l, values[i]])) }
}

const MoveButtons: React.FC<{
  name: string
  index: number
  count: number
  onMove: (to: number) => void
}> = ({ name, index, count, onMove }) => (
  <>
    <Button
      type="text"
      size="small"
      aria-label={`Move ${name} up`}
      icon={<FontAwesomeIcon icon={faArrowUp} />}
      disabled={index === 0}
      onClick={() => onMove(index - 1)}
    />
    <Button
      type="text"
      size="small"
      aria-label={`Move ${name} down`}
      icon={<FontAwesomeIcon icon={faArrowDown} />}
      disabled={index >= count - 1}
      onClick={() => onMove(index + 1)}
    />
  </>
)

/**
 * Scores of a scale table: option to number, the exceptions unscored. The
 * categories of a variable from the data get no number by themselves:
 * "Number from 1" numbers them in the order of the data. Each option is
 * named as the tables show it (`optionName`), its label as its title.
 */
const ScoresField: React.FC<{
  question: CatalogueQuestion
  scores: Record<string, number> | null
  overridden: boolean
  optionName: (label: string) => string
  onChange: (scores: Record<string, number>) => void
  onDefault: () => void
}> = ({ question, scores, overridden, optionName, onChange, onDefault }) => {
  const empty = !scores || Object.keys(scores).length === 0
  const fromData = question.source === "data"
  const setScore = (label: string, value: number | string | null): void => {
    const next = { ...(scores ?? {}) }
    if (typeof value === "number" && Number.isFinite(value)) next[label] = value
    else delete next[label]
    onChange(next)
  }
  return (
    <Field label="Scores">
      {empty && <p className="ts-hint">{fromData ? DATA_SCORES_HINT : SCORES_HINT}</p>}
      <table className="ts-scores">
        <tbody>
          {question.options.map((o) => (
            <tr key={o.label}>
              <td className="ts-scores__label" title={o.label}>
                {optionName(o.label)}
              </td>
              <td className="ts-scores__value">
                {o.exception ? (
                  <span className="ts-hint">not scored</span>
                ) : (
                  <InputNumber
                    size="small"
                    aria-label={`Score for ${optionName(o.label)}`}
                    value={scores?.[o.label] ?? null}
                    onChange={(v) => setScore(o.label, v)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="ts-actions">
        <Button
          size="small"
          onClick={() =>
            onChange(empty ? positionScores(question, true) : reverseScores(question, scores ?? {}))
          }
        >
          Reverse
        </Button>
        {fromData && empty && (
          <Button size="small" onClick={() => onChange(positionScores(question))}>
            Number from 1
          </Button>
        )}
        <Button size="small" disabled={!overridden} onClick={onDefault}>
          Use default scores
        </Button>
      </div>
    </Field>
  )
}

/**
 * The rows of a choice table: what it is shown as, every row in order with
 * its own controls, the nets, the difference rows (one row minus another),
 * the mean and NPS, and the scores of a scale.
 * While the table follows the questionnaire (for a variable from the data,
 * the prepared data) its rows are the defaults; the first edit fixes the
 * list as shown, with that edit applied. A lone yes or no field from the
 * data always has the rows Yes and No. A table from the data gets a mean,
 * NPS and scores only as a scale: its fields need not exclude each other,
 * and when it runs the engine checks that they do (the compiler and the
 * result step say what came of it). A grid of the data whose rows are
 * containers side by side, shown as one table (`showsSiblings`), is single
 * or multiple choice, and is laid out stacked (each row of the grid in
 * full) or one row each (the row chosen, for every row of the grid); its
 * rows here are those of every block. Every name reads as the tables show
 * it (the shown labels), the label itself as the title. A number table's
 * rows are each value (a switch) and its ranges (`RangeListEditor`), above
 * its statistics. A grid summary's are what each grid row shows, along which
 * dimension, and which grid rows (`GridSummaryRows`). Each table also says
 * whether its rows are sorted and whether those nobody gave show, following
 * the defaults or its own (`RowOrder`); an answer can be kept at the end
 * when sorting, as an exception always is.
 */
const RowsTab: React.FC<{ tableId: string }> = ({ tableId }) => {
  const inspected = useInspectedTable(tableId)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)
  const [adding, setAdding] = useState(false)
  const [differencing, setDifferencing] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  // The plan holds the value rows the table shows: no range can take one's name.
  const plan = useTabSpecStore((s) => selectPlan(s, tableId))
  if (!inspected) return null
  const { table, resolved, loaded } = inspected
  const fromData = table.source === "data"
  const names = shownNames(inspected.document.labels)

  if (table.kind === "numeric") {
    const ranges = resolved.rows.filter(isRangeRow)
    const valueNames = (plan?.layout ?? []).flatMap((s) => (s.kind === "row" && s.value ? [s.label] : []))
    // Stored as rows of the table, keys as the document reads them back; no
    // range left gives the table no rows of its own, as before any was added.
    const writeRanges = (next: RangeItem[]): void =>
      next.length === 0
        ? clearOverrides(tableId, ["rows"])
        : updateTable(tableId, {
            rows: next.map(
              (r): RangeRow => ({
                kind: "range",
                name: r.name,
                ...(r.min !== undefined ? { min: r.min } : {}),
                ...(r.max !== undefined ? { max: r.max } : {}),
              })
            ),
          })
    return (
      <div className="ts-tab">
        <Field label="Each value">
          <div className="ts-field__row">
            <span>Show each value</span>
            <Switch
              size="small"
              aria-label="Show each value"
              checked={table.values === true}
              onChange={(on) =>
                on ? updateTable(tableId, { values: true }) : clearOverrides(tableId, ["values"])
              }
            />
          </div>
          <p className="ts-hint">{EACH_VALUE}</p>
        </Field>
        <Field label="Ranges">
          <p className="ts-hint">{ranges.length > 0 ? RANGES : NO_RANGES}</p>
          <RangeListEditor
            ranges={ranges}
            onChange={writeRanges}
            label="Ranges of the table"
            others={valueNames}
          />
        </Field>
        {/* The values and the ranges stay in the order of the numbers: only the empty ones can go. */}
        <RowOrder tableId={tableId} rows="ranges" sortable={false} />
      </div>
    )
  }
  if (table.kind === "grid-summary") return <GridSummaryRows tableId={tableId} />
  const q = resolved.question
  if (!q) {
    const variable = names.variable(table.question)
    return (
      <p className="ts-hint">
        {fromData
          ? goneFromData(table.question, variable, loaded ? inspected.catalogue : null)
          : loaded
            ? `${variable} is no longer in the questionnaire.`
            : "The questionnaire is still loading."}
      </p>
    )
  }

  const rows = resolved.rows
  /** An option as the tables show it: its shown label, or its label. */
  const optionName = (label: string): string => names.option(q.label, label)
  // A grid of containers side by side, shown as one table: single or
  // multiple choice only (a scale of it is not one table).
  const acrossRows = q.siblings !== undefined && table.instance === null
  const choices = kindsForQuestion(q).filter(
    (k) => k !== "grid-summary" && !(acrossRows && k === "scale")
  )
  const scale = table.kind === "scale"
  const showAs = choices.length >= 2 && (
    <Field label="Show as">
      <Segmented<TableKind>
        size="small"
        value={table.kind}
        options={choices.map((k) => ({ value: k, label: KIND_TEXT[k] }))}
        onChange={(kind) => updateTable(tableId, { kind, rows: null })}
      />
      {fromData && scale && <p className="ts-hint">{DATA_SCALE}</p>}
    </Field>
  )
  const oneTable = showsSiblings(table, q)
  const layoutShown: SiblingLayout = table.siblingLayout === "row" ? "row" : "stacked"
  const gridRows = listNames(
    q.instances.map((i) => names.instance(q.label, i.id, i.label || i.id))
  )
  const rowChoices = oneTable ? siblingRowChoices(rows, optionName) : []
  const chosenRow = table.siblingRow?.trim() || null
  const layout = oneTable && (
    <>
      <Field label="Layout">
        <Segmented<SiblingLayout>
          size="small"
          value={layoutShown}
          options={LAYOUT_OPTIONS}
          onChange={(siblingLayout) => updateTable(tableId, { siblingLayout })}
        />
        <p className="ts-hint">
          {layoutShown === "row"
            ? `${gridRows}: one row each, the row chosen below, with its base in a note.`
            : `${gridRows}: each in full, one below the other, with its own base.`}
        </p>
      </Field>
      {layoutShown === "row" && (
        <Field label="Row shown">
          <Select
            size="small"
            showSearch
            aria-label="Row shown"
            className="ts-field__select"
            placeholder="Choose a row"
            value={chosenRow}
            options={rowChoices}
            optionFilterProp="search"
            notFoundContent="No row matches"
            onChange={(siblingRow: string) => updateTable(tableId, { siblingRow })}
          />
        </Field>
      )}
    </>
  )
  // One yes or no field: the compiler always gives it the rows Yes and No,
  // whatever rows are stored, so there is nothing to edit.
  if (isYesNoField(q))
    return (
      <div className="ts-tab">
        {showAs}
        <Field label="Rows">
          <p className="ts-hint">{YES_NO}</p>
          <ol className="ts-rows" aria-label="Rows of the table">
            {["Yes", "No"].map((name) => (
              <li key={name} className="ts-row">
                <span className="ts-row__name">{name}</span>
              </li>
            ))}
          </ol>
        </Field>
        {/* Always Yes, then No: only an empty one can go. */}
        <RowOrder tableId={tableId} rows="answers" sortable={false} />
      </div>
    )
  // The net's members are stored by their labels, and shown as the tables show them.
  const netOptions = q.options
    .filter((o) => !o.exception)
    .map((o) => ({ value: o.label, label: optionName(o.label), title: o.label }))
  // The rows a difference can be made of: the options shown (stored by their
  // labels, shown as the tables show them) and the nets, in the table's order.
  const differenceRows = rows.flatMap((r) =>
    r.kind === "option" && !r.hidden
      ? [{ value: r.label, label: r.rename?.trim() || optionName(r.label), title: r.label }]
      : r.kind === "net" && r.name.trim()
        ? [{ value: r.name.trim(), label: r.name.trim(), title: r.name.trim() }]
        : []
  )
  /** Writes the rows; an edit that changes nothing leaves the table as it is. */
  const write = (next: RowItem[]): void => {
    setRefusal(null)
    if (JSON.stringify(next) === JSON.stringify(rows)) return
    updateTable(tableId, { rows: next })
  }
  const edit = (fn: (current: RowItem[]) => RowItem[]): void => write(fn(materialise(resolved)))
  const move = (index: number) => (to: number) => edit((r) => moveRow(r, index, to))
  const remove = (index: number, name: string) => (
    <Button
      type="text"
      size="small"
      danger
      aria-label={`Remove ${name}`}
      icon={<FontAwesomeIcon icon={faTrash} />}
      onClick={() => edit((r) => removeItem(r, index))}
    />
  )
  const onAddNps = (): void => {
    const next = addNps(materialise(resolved), q, resolved.scores ?? {})
    if (typeof next === "string") setRefusal(next)
    else write(next)
  }
  /** The exceptions ("Don't know"): always kept at the end when the rows are sorted. */
  const exceptions = new Set(q.options.flatMap((o) => (o.exception ? [o.label] : [])))
  // Rows are keyed by what they are, so a field keeps its row when rows move.
  const seen = new Map<string, number>()
  const keyOf = (r: RowItem): string => {
    const base = `${r.kind}:${rowName(r)}`
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    return `${base}:${count}`
  }

  return (
    <div className="ts-tab">
      {showAs}
      {layout}
      <Field label="Rows">
        {table.rows === null ? (
          <p className="ts-hint">{fromData ? FOLLOWING_DATA : FOLLOWING}</p>
        ) : (
          <p className="ts-hint">
            {fromData ? FIXED_DATA : FIXED}{" "}
            <Button
              type="link"
              size="small"
              className="ts-link"
              onClick={() => clearOverrides(tableId, ["rows"])}
            >
              {fromData ? "Follow the prepared data again" : "Follow the questionnaire again"}
            </Button>
          </p>
        )}
        <ol className="ts-rows" aria-label="Rows of the table">
          {rows.map((r, i) => {
            // An option is named as the tables show it; its label is its title.
            const name = r.kind === "option" ? optionName(r.label) : rowName(r)
            const key = keyOf(r)
            const moveButtons = (
              <MoveButtons name={name} index={i} count={rows.length} onMove={move(i)} />
            )
            if (r.kind === "option") {
              const exception = exceptions.has(r.label)
              return (
                <li key={key} className={`ts-row${r.hidden ? " ts-row--hidden" : ""}`}>
                  <span className="ts-row__name" title={r.label}>
                    {name}
                  </span>
                  <div className="ts-row__controls">
                    <DraftInput
                      className="ts-row__rename"
                      placeholder={name}
                      ariaLabel={`Rename ${name}`}
                      value={r.rename ?? ""}
                      onCommit={(text) => {
                        const next = renameRow(materialise(resolved), r.label, text, optionName)
                        if (typeof next === "string") return next
                        write(next)
                      }}
                    />
                    {/* Kept at the end of its run when the rows are sorted; an exception always is. */}
                    <Button
                      type="text"
                      size="small"
                      className="ts-row__pin"
                      aria-label={`Keep ${name} at the end`}
                      aria-pressed={exception || r.anchor === true}
                      disabled={exception}
                      title={
                        exception
                          ? KEPT_EXCEPTION
                          : r.anchor
                            ? "Kept at the end when sorting: click to let it move"
                            : "Keep at the end when sorting"
                      }
                      icon={<FontAwesomeIcon icon={faThumbtack} />}
                      onClick={() => edit((all) => setAnchor(all, r.label, !r.anchor))}
                    />
                    {/* The dashboards' eye: pressed while the row is hidden. */}
                    <Button
                      type="text"
                      size="small"
                      className="ts-row__eye"
                      aria-label={`Hide ${name}`}
                      aria-pressed={r.hidden === true}
                      title={r.hidden ? "Hidden: click to show" : "Hide"}
                      icon={<FontAwesomeIcon icon={r.hidden ? faEyeSlash : faEye} />}
                      onClick={() => edit((all) => setHidden(all, r.label, !r.hidden))}
                    />
                    {moveButtons}
                  </div>
                </li>
              )
            }
            return (
              <li key={key} className={`ts-row ts-row--${r.kind}`}>
                <span className="ts-row__name">{name}</span>
                {r.kind === "net" && (
                  <span className="ts-row__members">
                    {r.members.map((m) => (
                      <Tag key={m} className="ts-tag" title={m}>
                        {optionName(m)}
                      </Tag>
                    ))}
                  </span>
                )}
                {r.kind === "difference" && (
                  <span className="ts-hint">
                    {r.plus} minus {r.minus}
                  </span>
                )}
                <div className="ts-row__controls">
                  {moveButtons}
                  {remove(i, name)}
                </div>
              </li>
            )
          })}
        </ol>
        <div className="ts-actions">
          <Button size="small" onClick={() => setAdding(true)}>
            Add net
          </Button>
          {/* One row minus another, as NPS is Promoters minus Detractors. */}
          <Button
            size="small"
            disabled={differenceRows.length < 2}
            onClick={() => setDifferencing(true)}
          >
            Add difference
          </Button>
          {/* Fields from the data need not exclude each other: a mean only on a scale, which the engine checks. */}
          {(!fromData || scale) && (
            <Button
              size="small"
              disabled={rows.some((r) => r.kind === "mean")}
              onClick={() => edit(addMean)}
            >
              Add mean
            </Button>
          )}
          {scale && resolved.scores && (
            <Button size="small" onClick={onAddNps}>
              Add NPS
            </Button>
          )}
        </div>
        {refusal && (
          <p className="ts-error" role="alert">
            {refusal}
          </p>
        )}
      </Field>
      {/* A grid of the data shown as one table keeps every block as it is. */}
      {!oneTable && <RowOrder tableId={tableId} rows="answers" sortable fromData={fromData} />}
      {scale && (
        <ScoresField
          question={q}
          scores={resolved.scores}
          overridden={table.scores !== null}
          optionName={optionName}
          onChange={(scores) => updateTable(tableId, { scores })}
          onDefault={() => clearOverrides(tableId, ["scores"])}
        />
      )}
      <AddNetModal
        open={adding}
        title="Add net"
        options={netOptions}
        onCancel={() => setAdding(false)}
        onConfirm={(name, members) => {
          const next = addNet(materialise(resolved), name, members)
          if (typeof next === "string") return next
          write(next)
          setAdding(false)
          return null
        }}
      />
      <AddDifferenceModal
        open={differencing}
        rows={differenceRows}
        onCancel={() => setDifferencing(false)}
        onConfirm={(name, plus, minus) => {
          const next = addDifference(materialise(resolved), name, plus, minus)
          if (typeof next === "string") return next
          write(next)
          setDifferencing(false)
          return null
        }}
      />
    </div>
  )
}

export default RowsTab
